import { createContext, useContext, useState } from 'react';

export const DateFormatContext = createContext({
  dateFmt: 'auto',
  setDateFmt: () => {},
  formatDate: () => '—',
  formatDateTime: () => '—',
});

const VALID_FMTS = new Set(['auto', 'mdy', 'dmy', 'iso']);

function normalize(fmt) {
  return VALID_FMTS.has(fmt) ? fmt : 'auto';
}

// Parse a date-only string (YYYY-MM-DD) as local midnight.
// Parse a SQLite datetime string ("YYYY-MM-DD HH:MM:SS") as UTC.
// Both forms arrive from the backend without timezone info.
function parseDate(str) {
  if (!str) return null;
  if (str.includes(' ')) return new Date(str.replace(' ', 'T') + 'Z'); // datetime → UTC
  if (str.includes('T')) return new Date(str);
  return new Date(str + 'T00:00:00'); // date-only → local midnight
}

export function formatDateWithFmt(str, fmt) {
  if (!str) return '—';
  const f = normalize(fmt);
  if (f === 'iso') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const d = parseDate(str);
    if (!d || isNaN(d.getTime())) return '—';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  const d = parseDate(str);
  if (!d || isNaN(d.getTime())) return '—';
  const locale = f === 'dmy' ? 'en-GB' : f === 'mdy' ? 'en-US' : undefined;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTimeWithFmt(str, fmt) {
  if (!str) return '—';
  const f = normalize(fmt);
  const d = parseDate(str);
  if (!d || isNaN(d.getTime())) return '—';
  if (f === 'iso') {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hr = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${mo}-${day} ${hr}:${min}`;
  }
  const locale = f === 'dmy' ? 'en-GB' : f === 'mdy' ? 'en-US' : undefined;
  return d.toLocaleString(locale, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

// Read the stored preference without a hook — for module-level helpers in Admin.js.
export function getStoredDateFmt() {
  return normalize(localStorage.getItem('dateFormat'));
}

export function DateFormatProvider({ children }) {
  const [dateFmt, setDateFmtState] = useState(
    () => normalize(localStorage.getItem('dateFormat'))
  );

  const setDateFmt = (fmt) => {
    const f = normalize(fmt);
    setDateFmtState(f);
    localStorage.setItem('dateFormat', f);
  };

  const formatDate = (str) => formatDateWithFmt(str, dateFmt);
  const formatDateTime = (str) => formatDateTimeWithFmt(str, dateFmt);

  return (
    <DateFormatContext.Provider value={{ dateFmt, setDateFmt, formatDate, formatDateTime }}>
      {children}
    </DateFormatContext.Provider>
  );
}

export function useDateFormat() {
  return useContext(DateFormatContext);
}
